import { useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { useChatSidebar, SIDEBAR_WIDTH, CHAT_WINDOW_WIDTH, MINIMIZED_CHAT_WIDTH, MINIMIZED_CHAT_HEIGHT, CHAT_GAP } from 'context/ChatSidebarContext';

import ChatsList from './ChatsList';
import DialogContactsSVG from 'constants/svg/DialogContactsSVG';
import DialogSVG from 'constants/svg/DialogSVG';
import { COMPONENTS } from 'constants/chatConstants';
import ChatContactsList from './ChatContactsList';
import DealEmailNotifications from './DealEmailNotifications';
import ExchangeChat, { CHAT_TYPE } from 'components/Chats/ExchangeChat';
import { getFormattedUserName } from 'helpers/chats';
import { useMedia } from 'hooks/useMedia';

import styles from './styles.module.scss';

export default function ChatsWindow() {
  const {
    isOpen,
    openChatIds,
    toggleSidebar,
    openChat,
    closeChat,
    toggleMinimizeChat,
    isChatMinimized,
    getChatDealData,
    popupChatData,
    closePopupChat,
  } = useChatSidebar();

  const { isDesktop } = useMedia();
  const [component, setComponent] = useState(COMPONENTS.CHAT);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920);
  // Для мобильной версии - активный чат внутри сайдбара (при клике из списка)
  const [mobileActiveChat, setMobileActiveChat] = useState(null);

  const conversations = useSelector((state) => state.conversations.items);
  const contacts = useSelector((state) => state.contacts.items);

  // Слушаем изменение размера окна
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Получаем полные данные чатов из redux по сохранённым ID
  const openChatsData = useMemo(() => {
    return openChatIds.map((employeeId) => {
      // Сначала ищем в conversations
      const conversation = conversations.find(
        (conv) => conv.employee?.id === employeeId
      );
      if (conversation) return conversation;

      // Если нет в conversations - ищем в contacts
      const contact = contacts.find(
        (c) => c.employee?.id === employeeId
      );
      if (contact) return contact;

      // Fallback - берём employee из dealData если есть, иначе минимальный объект
      const dealData = getChatDealData(employeeId);
      if (dealData?.employee) {
        return { employee: dealData.employee };
      }
      return { employee: { id: employeeId } };
    }).filter(chat => chat.employee?.id); // Убираем невалидные
  }, [openChatIds, conversations, contacts, getChatDealData]);

  // Разделяем чаты на развёрнутые и свёрнутые
  const { expandedChats, minimizedChats } = useMemo(() => {
    const expanded = [];
    const minimized = [];

    openChatsData.forEach((chat) => {
      const employeeId = chat.employee?.id;
      if (isChatMinimized(employeeId)) {
        minimized.push(chat);
      } else {
        expanded.push(chat);
      }
    });

    return { expandedChats: expanded, minimizedChats: minimized };
  }, [openChatsData, isChatMinimized]);

  // Проверяем помещаются ли открытые чаты и автосворачиваем
  const { visibleExpandedChats, autoMinimizedChats } = useMemo(() => {
    const availableWidth = windowWidth - SIDEBAR_WIDTH - 30;
    const visible = [];
    const autoMinimized = [];
    let usedWidth = 0;

    expandedChats.forEach((chat) => {
      if (usedWidth + CHAT_WINDOW_WIDTH <= availableWidth) {
        visible.push(chat);
        usedWidth += CHAT_WINDOW_WIDTH + CHAT_GAP;
      } else {
        autoMinimized.push(chat);
      }
    });

    return { visibleExpandedChats: visible, autoMinimizedChats: autoMinimized };
  }, [expandedChats, windowWidth]);

  // Все свёрнутые чаты (вручную + автоматически)
  const allMinimizedChats = useMemo(() => {
    return [...minimizedChats, ...autoMinimizedChats];
  }, [minimizedChats, autoMinimizedChats]);

  // Вычисляем позиции развёрнутых чатов
  const expandedPositions = useMemo(() => {
    const positions = [];
    let currentRight = SIDEBAR_WIDTH + 15;

    visibleExpandedChats.forEach(() => {
      positions.push({ right: currentRight });
      currentRight += CHAT_WINDOW_WIDTH + CHAT_GAP;
    });

    return positions;
  }, [visibleExpandedChats]);

  // Вычисляем позиции свёрнутых чатов
  const minimizedPositions = useMemo(() => {
    const positions = [];
    let currentRight = SIDEBAR_WIDTH + 15;

    allMinimizedChats.forEach(() => {
      positions.push({ right: currentRight });
      currentRight += MINIMIZED_CHAT_WIDTH + CHAT_GAP;
    });

    return positions;
  }, [allMinimizedChats]);

  const componentToRender = () => {
    if (component === COMPONENTS.CHAT) {
      return (
        <ChatsList
          onChatClick={handleOpenChatWindow}
          openedChats={openChatIds}
        />
      );
    }

    if (component === COMPONENTS.CONTACT) {
      return (
        <ChatContactsList
          onChatClick={handleContactChatWindow}
          openedChats={openChatIds}
        />
      );
    }
  };

  const handleContactChatWindow = (el) => {
    const employeeId = el.employee?.id;
    if (employeeId) {
      if (isDesktop) {
        openChat(employeeId);
      } else {
        // На мобильных открываем чат внутри сайдбара
        setMobileActiveChat(el);
      }
    }
  };

  const handleOpenChatWindow = (el) => {
    const employeeId = el.employee?.id;
    if (employeeId) {
      if (isDesktop) {
        openChat(employeeId);
      } else {
        // На мобильных открываем чат внутри сайдбара
        setMobileActiveChat(el);
      }
    }
  };

  const handleCloseMobileChat = () => {
    setMobileActiveChat(null);
  };

  const handleCloseChatWindow = (employeeId) => {
    closeChat(employeeId);
  };

  const sidebarEl = document.getElementById('sidebar');
  if (!sidebarEl) return null;

  return ReactDOM.createPortal(
    <>
      <div className={cn(styles.sidebar, isOpen && styles['sidebar--open'])}>
        <div className={styles.sidebar__content}>
          {/* Мобильный чат - показываем внутри сайдбара (при клике из списка) */}
          {!isDesktop && mobileActiveChat ? (
            <ExchangeChat
              mode={CHAT_TYPE.singleChat}
              onCloseClick={handleCloseMobileChat}
              employee={mobileActiveChat.employee}
              conversationId={mobileActiveChat.id}
              handleChatWindowClose={handleCloseMobileChat}
            />
          ) : (
            <>
              <div className={styles.sidebar__header}>
                <button className={styles.sidebar__closeBtn} onClick={toggleSidebar}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
                <div className={styles.sidebar__buttons}>
                  <button
                    onClick={() => setComponent(COMPONENTS.CHAT)}
                    className={styles.sidebar__button}
                  >
                    <DialogSVG component={component} />
                  </button>
                  <button
                    onClick={() => setComponent(COMPONENTS.CONTACT)}
                    className={styles.sidebar__button}
                  >
                    <DialogContactsSVG component={component} />
                  </button>
                </div>
              </div>

              <DealEmailNotifications isActive={isOpen} />

              <div className={styles.sidebar__list}>
                {componentToRender()}
              </div>
            </>
          )}
        </div>

      {/* Свёрнутые чаты - внизу */}
      {allMinimizedChats.map((conversation, index) => {
        const employeeId = conversation.employee?.id;
        const position = minimizedPositions[index];
        const userName = getFormattedUserName(conversation.employee);
        const companyName = conversation.employee?.company?.name || conversation.employee?.organization_name;
        const hasUnread = conversation.is_seen === 0 || conversation.is_seen === false;

        return (
          <div
            key={`min-${employeeId}`}
            className={cn(
              styles.sidebar__chatWindow,
              styles['sidebar__chatWindow--minimized'],
              hasUnread && styles['sidebar__chatWindow--unread']
            )}
            style={{ right: position?.right || 0 }}
          >
            <div className={styles.sidebar__minimizedChat}>
              <div
                className={styles.sidebar__minimizedHeader}
                onClick={() => toggleMinimizeChat(employeeId)}
              >
                {conversation.employee?.picture ? (
                  <span
                    className={styles.sidebar__minimizedAvatar}
                    style={{ backgroundImage: `url('${conversation.employee.picture}')` }}
                  />
                ) : (
                  <span className={cn(styles.sidebar__minimizedAvatar, styles['sidebar__minimizedAvatar--empty'])} />
                )}
                <div className={styles.sidebar__minimizedInfo}>
                  <span className={styles.sidebar__minimizedName}>{userName}</span>
                  {companyName && <span className={styles.sidebar__minimizedCompany}>{companyName}</span>}
                </div>
              </div>
              <button
                className={styles.sidebar__minimizedClose}
                onClick={() => handleCloseChatWindow(employeeId)}
              >
                ×
              </button>
            </div>
          </div>
        );
      })}

      {/* Развёрнутые чаты - над свёрнутыми */}
      {visibleExpandedChats.map((conversation, index) => {
        const employeeId = conversation.employee?.id;
        const position = expandedPositions[index];
        const bottomOffset = allMinimizedChats.length > 0 ? MINIMIZED_CHAT_HEIGHT + CHAT_GAP : 0;
        const chatKey = `chat-${employeeId}-${conversation.id || 'new'}`;
        const hasUnread = conversation.is_seen === 0 || conversation.is_seen === false;

        return (
          <div
            key={chatKey}
            className={cn(
              styles.sidebar__chatWindow,
              hasUnread && styles['sidebar__chatWindow--unread']
            )}
            style={{
              right: position?.right || 0,
              bottom: bottomOffset
            }}
          >
            <button
              className={styles.sidebar__minimizeBtn}
              onClick={() => toggleMinimizeChat(employeeId)}
              title="Свернуть"
            >
              −
            </button>
            <ExchangeChat
              key={`exchange-${employeeId}-${conversation.id || 'new'}`}
              mode={CHAT_TYPE.singleChat}
              onCloseClick={() => handleCloseChatWindow(employeeId)}
              employee={conversation.employee}
              conversationId={conversation.id}
              handleChatWindowClose={() => handleCloseChatWindow(employeeId)}
              item={getChatDealData(employeeId)?.item}
              type={getChatDealData(employeeId)?.type}
            />
          </div>
        );
      })}
      </div>

      {/* Попап-чат (модалка) - открывается при клике "Перейти в чат" */}
      {popupChatData && (
        <div className={styles.chatPopup}>
          <div className={styles.chatPopup__overlay} onClick={closePopupChat} />
          <div className={styles.chatPopup__content}>
            <ExchangeChat
              mode={CHAT_TYPE.singleChat}
              onCloseClick={closePopupChat}
              employee={popupChatData.employee}
              conversationId={popupChatData.conversationId}
              handleChatWindowClose={closePopupChat}
              item={popupChatData.item}
              type={popupChatData.type}
            />
          </div>
        </div>
      )}
    </>,
    sidebarEl,
  );
}
